import { broadcastQueryClient } from "@tanstack/query-broadcast-client-experimental";
import { QueryClient, QueryClientProvider, type QueryKey, hashKey, useQueryClient } from "@tanstack/react-query";
import { logger } from "@artis/logger";
import { createContext, useContext, useMemo, useRef } from "react";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Number.POSITIVE_INFINITY,
      retry: 3,
    },
    mutations: {
      onError: (error) => logger.error("Mutation error", error),
    },
  },
});

broadcastQueryClient({
  // biome-ignore lint/suspicious/noExplicitAny: no type
  queryClient: queryClient as any,
  broadcastChannel: "artis-query",
});

export const AppQueryProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <PendingChangesContext.Provider value={useMemo(() => new Map<string>(), [])}>
        {children}
      </PendingChangesContext.Provider>
    </QueryClientProvider>
  );
};

const PendingChangesContext = createContext(new Map<string>());

export const usePendingChanges = <P,>(key: QueryKey, init: () => P) => {
  "use no memo"; // FIXME: REACT-COMPILER - Error: Cannot access refs during render

  const pending = useContext(PendingChangesContext);
  const initRef = useRef(init);
  initRef.current = init;

  const hash = hashKey(key);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We only want to re-run if pending, hash or initRef changes
  return useMemo(() => {
    if (!pending.has(hash)) pending.set(hash, initRef.current());
    return pending.get(hash) as P;
  }, [pending, hash, initRef]);
};

export const createQueryCacheHook =
  <T,>(queryKey: QueryKey) =>
  () => {
    const qry = useQueryClient();
    return {
      queryKey,
      get: () => qry.getQueryData<T>(queryKey),
      set: (data: T) => qry.setQueryData<T>(queryKey, data),
      invalidate: () => qry.invalidateQueries({ queryKey }),
      refetch: () => qry.refetchQueries({ queryKey }),
      remove: () => qry.removeQueries({ queryKey }),
    };
  };
